import { cloneDeep } from 'lodash';
import {
  AccountClient,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA
} from '@nile/graphql';
import { COMMON_CONSTANTS } from '@nile/components';

const getAccessor = (col) => {
  switch (col.name) {
    case 'prioritizedId':
      return 'displayId';
    case 'displayId':
      return 'displayId';
    case 'followers':
      return undefined;
    case 'resolutionTimer':
      return 'resolutionDueTimeLeft';
    case 'taskSummary':
      return 'totalTasks';
    default:
      return col.name;
  }
};

const getMethodName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'getAllCategory';
    case 'subcause':
      return 'getAllCause';
    default:
      return 'name';
  }
};

const getKeyName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'subCategories';
    case 'subcause':
      return 'subCauses';
    default:
      return 'name';
  }
};

const generateOptions = (f, data) => {
  const options = [];
  let allValues = [];
  if (f.isCustom) {
    allValues = data.getCustomFieldByName.options.map((el) => {
      return {
        name: el,
        value: el,
        label: el
      };
    });
    return allValues;
  }
  const methodName = getMethodName(f.name);
  const key = getKeyName(f.name);
  switch (f.name) {
    case 'client.name':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'site.name':
      allValues = data.getClientSiteList.sites.map((el) => {
        return {
          ...el,
          name: el.siteId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'subcategory':
    case 'subcause':
      data[methodName].forEach((el) => {
        const subValues = el[key].map((c) => {
          return {
            name: c.name,
            value: c.name,
            label: c.name
          };
        });
        allValues = allValues.concat(subValues);
      });
      break;
    default:
      allValues = [];
  }
  return allValues;
};

const getType = (col) => {
  switch (col.name) {
    case 'name':
      return 'Integration';
    case 'destination.name':
      return 'Client';
    case 'stripedestination.name':
      return 'StripeClient';
    case 'paymentdestination.name':
      return 'PaymentCell';
    case 'servicedestination.name':
      return 'ServiceItem';
    case 'CREATE':
      return 'Ignore';
    case 'serviceItemType':
      return 'Type';
    case 'packageName':
      return 'Package';
    case 'protectionPlan':
      return 'ProtectionPlan';
    case 'dateTime':
      return 'Date';
    case 'workItem':
      return 'WorkItem';
    case 'timespent':
      return 'Timer';
    default:
      return 'Text';
  }
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
      return 'displayId';
    default:
      return f.name;
  }
};

const isEditable = (col) => {
  switch (col.name) {
    case 'date':
      return true;
    default:
      return false;
  }
};

/**
 * Config function for Header type to distinguish if it's select all type or not
 */
const isSelectAll = (col) => {
  switch (col.name) {
    case 'CREATE':
    case 'IGNORE':
      return true;
    default:
      return false;
  }
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

const isSortable = (col) => {
  switch (col.name) {
    case 'source.name':
    case 'destination.name':
    case 'servicedestination.name':
    case 'stripedestination.name':
    case 'CREATE':
    case 'serviceItemType':
    case 'name':
      return false;
    default:
      return true;
  }
};

const isFilterable = (col) => {
  switch (col?.name) {
    // case 'name':
    case 'createdBy.name':
    case 'createdBy':
      return true;
    case 'source.name':
    case 'name':
      return col?.enableFilter || false;
    default:
      return false;
  }
};

const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'client.name':
    case 'site.name':
    case 'subcategory':
    case 'subcause':
      return true;
    default:
      return false;
  }
};

const updateConditions = (params) => {
  const { condition, selectedField, viewConditions, setConditions } = params;
  if (condition?.operands?.length) {
    const currentConditions = cloneDeep(viewConditions) || {
      joinOperator: 'AND',
      operands: []
    };
    let updatedCondition = {};
    const ruleIdx = currentConditions.operands.findIndex((el) =>
      selectedField.dataType === 'entity'
        ? el.attribute === `${selectedField.name}.name`
        : el.attribute === getRuleAttribute(selectedField)
    );

    if (ruleIdx > -1) {
      currentConditions.operands[ruleIdx] = condition.operands[0];
      updatedCondition = currentConditions;
    } else {
      updatedCondition = {
        ...currentConditions,
        operands: currentConditions.operands.concat(condition.operands)
      };
    }
    setConditions(updatedCondition);
  } else {
    const currentConditions = cloneDeep(viewConditions);
    if (currentConditions) {
      const ruleIdx = currentConditions.operands.findIndex((el) =>
        selectedField.dataType === 'entity'
          ? el.attribute === `${selectedField.name}.name`
          : el.attribute === getRuleAttribute(selectedField)
      );
      if (ruleIdx > -1) {
        currentConditions.operands.splice(ruleIdx, 1);
        setConditions(
          currentConditions?.operands?.length
            ? {
                ...currentConditions,
                operands: currentConditions.operands
              }
            : null
        );
      }
    }
  }
};

function getQuery(property) {
  switch (property.name) {
    case 'technician':
    case 'createdBy.name':
    case 'createdBy':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function getNameKey(property) {
  switch (property.name) {
    case 'technician':
    case 'createdBy.name':
    case 'createdBy':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

const fetchValues = async (client, query, params) => {
  console.log('fetching values', client, query, params);
  const attribute = 'name';
  const nameKey = getNameKey(params.property);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'name',
            operator: 'startsWith',
            value: params.inputValue
          }
        ]
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];

  console.log(allOptions);
  return allOptions;
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

const getBitdefenderCellTypes = (col) => {
  switch (col.name) {
    case 'packageName':
      return 'Package';
    default:
      return 'Text';
  }
};

const getAcronisCellTypes = (col) => {
  switch (col.name) {
    case 'protectionPlan':
      return 'ProtectionPlan';
    default:
      return 'Text';
  }
};

export {
  getDefaultValue,
  checkDynamicQuery,
  isFilterable,
  isSortable,
  getAccessor,
  getType,
  isEditable,
  getRuleAttribute,
  updateConditions,
  getAsyncOptions,
  fetchValues,
  generateOptions,
  isSelectAll,
  getBitdefenderCellTypes,
  getAcronisCellTypes
};
